<template>
  <v-dialog
    :value="show"
    fullscreen
    transition="dialog-bottom-transition"
    @input="$emit('update:show', $event)"
  >
    <v-card rounded="0">
      <v-toolbar dark color="primary" rounded="0">
        <v-toolbar-title>Create a New Complex Group</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn color="white" text @click="handleClose"
            >Close <v-icon color="white" class="ml-2">mdi-close</v-icon></v-btn
          >
          <v-btn
            color="white"
            text
            dark
            @click="handleCreate"
            :disabled="!formValid || rules === null || !rulesValid"
            >Create <v-icon color="white" class="ml-2">mdi-content-save</v-icon></v-btn
          >
        </v-toolbar-items>
      </v-toolbar>
      <v-divider />
      <v-card-text class="pa-6">
        <v-form ref="complexGroupForm" v-model="formValid">
          <v-text-field
            outlined
            class="pa-2"
            v-model="groupDisplayName"
            :rules="[
              (v) => !!v || 'Group name is required',
              (v) => !!(v && v.length < 255) || 'Group name should be shorter',
            ]"
            label="Group name*"
          ></v-text-field>
          <v-switch
            class="pa-2"
            v-if="hasSubClients"
            v-model="includeSubClientsValue"
            :label="`Include Sub-Clients`"
            hide-details
          ></v-switch>
          <p class="text-left pl-2">Note: Complex groups don't support external users</p>
          <div class="d-flex align-center mb-3">
            <v-divider />
            <h3 class="mx-5">Group Rule Creator</h3>
            <v-divider />
          </div>
          <div class="py-4 px-10 d-flex justify-space-between mr-3">
            <RuleBuilder
              @close-dialog="$emit('close-rule-builder')"
              @refresh-estimate="$emit('refresh-estimate')"
              :useMetrics="true"
              :useUserAttributes="true"
              :showSaveButton="false"
              :showExitButton="false"
              context="Groups"
              :trigger="ruleBuilderTrigger"
              :rules="rules"
              :metrics="metrics"
              :modules="modules"
              :name="groupDisplayName"
              :description="groupDisplayName"
              @update-rules="handleRulesUpdate"
              @update-name="handleNameUpdate"
              @update-description="handleDescriptionUpdate"
              :estimatedParticipants="estimatedParticipants"
              :loadingEstimatedParticipants="loadingEstimatedParticipants"
              :disableEstimatedParticipants="disableEstimatedParticipants"
              :clientId="clientId"
              :includeSubClients="includeSubClientsValue"
              :readOnly="readOnly"
              :hideTextFields="true"
            ></RuleBuilder>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="pr-16 pb-4">
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="handleClose"> Close </v-btn>
        <v-btn
          color="blue darken-1"
          @click="handleCreate"
          :disabled="!formValid || rules === null || !rulesValid"
        >
          <span class="white--text">Create</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import RuleBuilder from "@/components/RuleBuilderV3.vue";

export default {
  name: "NewComplexGroupDialog",
  components: {
    RuleBuilder,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    hasSubClients: {
      type: Boolean,
      default: false,
    },
    includeSubClients: {
      type: Boolean,
      default: false,
    },
    ruleBuilderTrigger: {
      type: Boolean,
    },
    rules: {
      type: [Object, Array],
      default: null,
    },
    rulesValid: {
      type: Boolean,
      default: false,
    },
    metrics: {
      type: Array,
      default: () => [],
    },
    modules: {
      type: Array,
      default: () => [],
    },
    estimatedParticipants: {
      type: [Number, String],
      default: 0,
    },
    loadingEstimatedParticipants: {
      type: Boolean,
      default: false,
    },
    disableEstimatedParticipants: {
      type: Boolean,
      default: false,
    },
    clientId: {
      type: [Number, String],
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      groupDisplayName: "",
    };
  },
  computed: {
    includeSubClientsValue: {
      get() {
        return this.includeSubClients;
      },
      set(value) {
        this.$emit("update:includeSubClients", value);
      },
    },
  },
  methods: {
    handleCreate() {
      if (this.formValid && this.rules !== null && this.rulesValid) {
        this.$emit("create", {
          groupDisplayName: this.groupDisplayName,
          includeSubClients: this.includeSubClientsValue,
        });
      }
    },
    handleClose() {
      this.resetForm();
      this.$emit("close");
    },
    handleRulesUpdate(rules) {
      this.$emit("update-rules", rules);
    },
    handleNameUpdate(name) {
      this.groupDisplayName = name;
      this.$emit("update-name", name);
    },
    handleDescriptionUpdate(description) {
      this.$emit("update-description", description);
    },
    resetForm() {
      this.groupDisplayName = "";
      this.formValid = false;
      if (this.$refs.complexGroupForm) {
        this.$refs.complexGroupForm.reset();
      }
    },
  },
  watch: {
    show(val) {
      if (!val) {
        this.resetForm();
      }
    },
    groupDisplayName: {
      handler(val) {
        this.$emit("update-name", val);
      },
      immediate: true,
    },
  },
};
</script>

<style scoped></style>
